import session from '@/auth/models/session'

interface TicketOptions {
  additionalRequestInformation?: string
  email?: string
  formId?: string
  name?: string
  noSuggestions?: boolean
  orderNumber?: string
  productType?: string
  corporateDesignNamesOrUrls?: string
  corporateTopic?: /** Quote request */
  | 'corp-quote'
    /** Help placing an order */
    | 'corp-op'
    /** Sample request */
    | 'corp-sample'
    /** Change/cancel existing order */
    | 'corp-oa'
    /** Report a received order issue */
    | 'corp-oi'
    /** General inquiry */
    | 'corp-ge'
  requestInformation?: string
  subject?: string
  topic?: string
}

interface ProductQuoteTicketOptions {
  itemShortTitle: string
  itemUrl: string
}

const corporateFormId = '7792449776663'

export const createTicketUrl = (
  ticketType: string,
  options: TicketOptions
): string => {
  // As we create new tickets check their length and make sure without the customer name & email the tickets don't
  // pass 1700 characters since most browsers cap URLs at 2000 characters.
  switch (ticketType) {
    // Each of these ticket types allow CSRs to define Zendesk Triggers which automatically add tags to these tickets
    // when they're created which will allow them to place the ticket in the right triage view automatically.
    case 'MA1':
      return assembleTicketUrl(fillMa1TicketOptions(options))
    case 'RO1':
      return assembleTicketUrl(fillRo1TicketOptions(options))
    default:
      return 'https://help.paperculture.com/hc/en-us/requests/new'
  }
}

// The MA1 ticket type is for orders with shipments that may or may not be at the printer. Only CSRs are capable
// of canceling these orders if they're at the printer. MA stands for My Account and 1 references that it is the
// first ticket type coming from the My Account page.
export const fillMa1TicketOptions = (options: TicketOptions): TicketOptions => {
  options = { ...options }
  options.subject =
    options.subject || 'Cancellation was unavailable for my order'
  options.topic = options.topic || 'oa'
  const additionalRequestInformation =
    options.additionalRequestInformation || ''
  delete options.additionalRequestInformation
  options.requestInformation =
    '<p>[Please describe why you need to cancel your order and if any changes are ' +
    'necessary, list changes here.]</p>' +
    additionalRequestInformation +
    '<p>Ticket Type: MA1</p>'
  return options
}

// The RO1 ticket type is for the reorder page when you want to make design changes to your reorder. Since the proof is
// immutable a CSR must clone it and make customer dictated edits in a fresh proof.
// RO stands for Reorder and 1 references that it is the first ticket type coming from the Reorder page.
export const fillRo1TicketOptions = (options: TicketOptions): TicketOptions => {
  options = { ...options }
  options.subject = options.subject || 'Reorder card with edits'
  options.topic = options.topic || 'dr2'
  const additionalRequestInformation =
    options.additionalRequestInformation || ''
  delete options.additionalRequestInformation
  options.requestInformation =
    "<p>[Please describe what edits you'd like made on your card here.]</p>" +
    additionalRequestInformation +
    '<p>Ticket Type: RO1</p>'
  return options
}

export const assembleTicketUrl = (options: TicketOptions): string => {
  const variableToUrlParamPairings: Record<string, string> = {
    email: 'tf_anonymous_requester_email',
    formId: 'ticket_form_id',
    subject: 'tf_subject',
    requestInformation: 'tf_description',
    name: 'tf_26318827',
    topic: 'tf_24733008',
    orderNumber: 'tf_24577498',
    productType: 'tf_24747587',
    corporateDesignNamesOrUrls: 'tf_7769730947479',
    corporateTopic: 'tf_7581344393111'
  }

  return (
    'https://help.paperculture.com/hc/en-us/requests/new?' +
    Object.keys(options)
      .filter(
        (key) => variableToUrlParamPairings[key] && key !== 'noSuggestions'
      )
      .map(
        (key) =>
          variableToUrlParamPairings[key] +
          '=' +
          encodeURIComponent(options[key as keyof TicketOptions] || '')
      )
      .join('&') +
    // If the hash includes no_suggestions, suggested articles are hidden on the ticket form
    (options.noSuggestions ? '#no_suggestions' : '')
  )
}

export const createProductQuoteTicketUrl = (
  options: ProductQuoteTicketOptions
): string => {
  return assembleTicketUrl({
    formId: corporateFormId,
    email: session.getUserData().email,
    subject: `Personalized quote for ${options.itemShortTitle}`,
    corporateTopic: 'corp-quote',
    corporateDesignNamesOrUrls: options.itemUrl,
    // Hide suggested articles based on the subject because they're not relevant and are distracting
    // for a customer getting a quote.
    noSuggestions: true
  })
}
